import { Link } from "@inertiajs/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import React from "react";
import { useScreenSize } from "../../hooks/useScreenSize";

interface ItemCardProps {
  href: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
  className?: string;
}

interface ItemCardImageProps {
  src?: string | null;
  fallbackIcon?: IconDefinition;
  children?: React.ReactNode;
  style?: React.CSSProperties;
}

interface ItemCardContentProps {
  children: React.ReactNode;
  style?: React.CSSProperties;
}

interface ItemCardTitleProps {
  badge?: string;
  badgeColor?: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
}

interface ItemCardDescriptionProps {
  children: React.ReactNode;
  style?: React.CSSProperties;
}

interface ItemCardMetaProps {
  children: React.ReactNode;
  style?: React.CSSProperties;
}

interface ItemCardActionsProps {
  children: React.ReactNode;
  style?: React.CSSProperties;
}

function ItemCard({ href, children, style, className }: ItemCardProps) {
  const { isSmall } = useScreenSize();

  return (
    <Link href={href} style={{ textDecoration: 'none', display: 'block', maxWidth: '100%' }}>
      <div
        className={className || ''}
        style={{
          display: 'flex',
          flexDirection: isSmall ? 'column' : 'row',
          height: isSmall ? 'auto' : '200px',
          width: '100%',
          maxWidth: '100%',
          border: '1px solid var(--color-base-300)',
          overflow: 'hidden',
          transition: 'all 0.3s ease',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
          cursor: 'pointer',
          ...style,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-0.5px)';
          e.currentTarget.style.boxShadow = '0 8px 16px rgba(0, 0, 0, 0.12)';
          e.currentTarget.style.borderColor = 'var(--color-primary)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)';
          e.currentTarget.style.borderColor = 'var(--color-base-300)';
        }}
      >
        {children}
      </div>
    </Link>
  );
}

function ItemCardImage({ src, fallbackIcon, children, style }: ItemCardImageProps) {
  const { isSmall } = useScreenSize();

  const imageContainerStyles: React.CSSProperties = {
    flexShrink: 0,
    height: isSmall ? '180px' : '100%',
    width: isSmall ? '100%' : '35%',
    ...style,
  };

  if (children) {
    // Custom content (e.g., 2x2 grid for navigations)
    return (
      <div style={imageContainerStyles}>
        {children}
      </div>
    );
  }

  if (src) {
    return (
      <div
        style={{
          position: 'relative',
          overflow: 'hidden',
          ...imageContainerStyles,
        }}
      >
        {/* Blurred background for small screens */}
        {isSmall && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: `url(${src})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              filter: 'blur(20px)',
              transform: 'scale(1.1)',
            }}
          />
        )}
        {/* Main image */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `url(${src})`,
            backgroundSize: isSmall ? 'contain' : 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        />
      </div>
    );
  }

  return (
    <div
      style={{
        backgroundColor: 'var(--color-base-300)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        ...imageContainerStyles,
      }}
    >
      {fallbackIcon && (
        <FontAwesomeIcon
          icon={fallbackIcon}
          style={{
            color: 'var(--color-primary)',
            fontSize: '96px',
            opacity: 0.7,
          }}
        />
      )}
    </div>
  );
}

function ItemCardContent({ children, style }: ItemCardContentProps) {
  return (
    <div style={{
      flex: 1,
      backgroundColor: 'var(--color-white)',
      display: 'flex',
      flexDirection: 'column',
      padding: '10px',
      gap: '12px',
      ...style,
    }}>
      {children}
    </div>
  );
}

function ItemCardTitle({ badge, badgeColor, children, style }: ItemCardTitleProps) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      ...style,
    }}>
      {badge && (
        <span style={{
          backgroundColor: badgeColor || 'var(--color-primary)',
          color: 'white',
          padding: '2px 6px',
          fontSize: '10px',
          fontWeight: 'bold',
          borderRadius: '3px',
          flexShrink: 0,
        }}>
          {badge}
        </span>
      )}
      <h2 className="text-2xl font-bold" style={{
        margin: 0,
        color: 'var(--color-neutral)',
        lineHeight: '1.3',
        overflow: 'hidden',
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
      }}>
        {children}
      </h2>
    </div>
  );
}

function ItemCardDescription({ children, style }: ItemCardDescriptionProps) {
  const { isSmall } = useScreenSize();

  return (
    <div
      style={{
        position: 'relative',
        color: 'var(--color-base-content)',
        fontSize: '14px',
        lineHeight: '1.6',
        flex: isSmall ? 'none' : '1 1 0',
        minHeight: isSmall ? '60px' : 0,
        overflow: isSmall ? 'visible' : 'hidden',
        ...style,
      }}
    >
      <div
        style={{
          maxHeight: isSmall ? 'none' : '100%',
          overflow: isSmall ? 'visible' : 'hidden',
        }}
      >
        {children}
      </div>
      {!isSmall && (
        <div
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '40px',
            background: 'linear-gradient(to bottom, transparent, var(--color-white))',
            pointerEvents: 'none',
          }}
        />
      )}
    </div>
  );
}

function ItemCardMeta({ children, style }: ItemCardMetaProps) {
  return (
    <div style={style}>
      <p style={{
        fontSize: '13px',
        color: 'var(--color-base-content)',
        opacity: 0.7,
        margin: 0,
        fontWeight: 500,
      }}>
        {children}
      </p>
    </div>
  );
}

function ItemCardActions({ children, style }: ItemCardActionsProps) {
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginTop: 'auto',
      ...style,
    }}>
      {children}
    </div>
  );
}

// Attach sub-components to main component
ItemCard.Image = ItemCardImage;
ItemCard.Content = ItemCardContent;
ItemCard.Title = ItemCardTitle;
ItemCard.Description = ItemCardDescription;
ItemCard.Meta = ItemCardMeta;
ItemCard.Actions = ItemCardActions;

export default ItemCard;
